import { TutorStatus } from '@app/types/tutors/enums'

import type { Dayjs } from 'dayjs'
import type { LocationQuery, LocationQueryRaw } from 'vue-router'
import type { TutorBillingDateRange, TutorBillingFilters } from '@ts-tutor-billing/interfaces'
import type { TriState } from '@ts-tutor-billing/types'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'
import { TutorBillingChangeType, TutorBillingDatePreset, TutorBillingFlag } from '@ts-tutor-billing/enums'

import { TUTOR_BILLING_FLAGS } from '@config/tutor-billing'
import { getFirstQueryValue, normalizeArrayQuery } from '@helpers/route'
import dayjs from 'dayjs'

export const parseTriState = (value: unknown): TriState => {
  const raw = getFirstQueryValue(value)
  if (raw === '1') return true
  if (raw === '0') return false
  return null
}

export const serializeTriState = (value: TriState): string | undefined => {
  if (value === null) return undefined
  return value ? '1' : '0'
}

const toEnumValue = <T extends string>(value: unknown, allowed: readonly T[]): T | null => {
  const raw = getFirstQueryValue(value)
  return allowed.find(item => item === raw) ?? null
}

const toEnumValues = <T extends string>(value: unknown, allowed: readonly T[]): T[] =>
  (normalizeArrayQuery(value) ?? []).filter((item): item is T => allowed.some(allowedItem => allowedItem === item))

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/

const toDateValue = (value: unknown): string | null => {
  const raw = getFirstQueryValue(value)
  if (raw === null || !DATE_PATTERN.test(raw)) return null
  return dayjs(raw).format(DateFormat.DefaultDate) === raw ? raw : null
}

export const getDatePresetRange = (preset: TutorBillingDatePreset, now: Dayjs): TutorBillingDateRange | null => {
  const day = (d: Dayjs): string => d.format(DateFormat.DefaultDate)
  switch (preset) {
    case TutorBillingDatePreset.Today: return { from: day(now), to: day(now) }
    case TutorBillingDatePreset.Yesterday: return { from: day(now.subtract(1, 'day')), to: day(now.subtract(1, 'day')) }
    case TutorBillingDatePreset.Last7Days: return { from: day(now.subtract(6, 'day')), to: day(now) }
    case TutorBillingDatePreset.Last30Days: return { from: day(now.subtract(29, 'day')), to: day(now) }
    case TutorBillingDatePreset.ThisMonth: return { from: day(now.startOf('month')), to: day(now.endOf('month')) }
    case TutorBillingDatePreset.LastMonth: {
      const prev = now.subtract(1, 'month')
      return { from: day(prev.startOf('month')), to: day(prev.endOf('month')) }
    }
    case TutorBillingDatePreset.ThisYear: return { from: day(now.startOf('year')), to: day(now.endOf('year')) }
    case TutorBillingDatePreset.Custom: return null
  }
}

export const resolveTutorBillingDateRange = (filters: TutorBillingFilters, now: Dayjs): TutorBillingDateRange | null => {
  if (!filters.datePreset) return null
  if (filters.datePreset !== TutorBillingDatePreset.Custom) return getDatePresetRange(filters.datePreset, now)
  return filters.dateFrom && filters.dateTo ? { from: filters.dateFrom, to: filters.dateTo } : null
}

export const getTutorBillingFilterParams = (filters: TutorBillingFilters, now: Dayjs): Record<string, unknown> => {
  const dateRange = resolveTutorBillingDateRange(filters, now)

  return {
    q: filters.search,
    tutor_id: filters.tutorIds.length ? filters.tutorIds : undefined,
    payment_service: filters.paymentServices.length ? filters.paymentServices : undefined,
    tutor_status: filters.tutorStatuses.length ? filters.tutorStatuses : undefined,
    change_type: filters.changeType,
    change_date_from: dateRange?.from,
    change_date_to: dateRange?.to,
    ...Object.fromEntries(TUTOR_BILLING_FLAGS.map(flag => [flag, serializeTriState(filters.flags[flag])])),
  }
}

export const emptyTutorBillingFilters = (): TutorBillingFilters => ({
  search: null,
  tutorIds: [],
  paymentServices: [],
  tutorStatuses: [],
  changeType: null,
  datePreset: null,
  dateFrom: null,
  dateTo: null,
  flags: Object.fromEntries(TUTOR_BILLING_FLAGS.map(flag => [flag, null])) as Record<TutorBillingFlag, TriState>,
})

export const queryToFilters = (query: LocationQuery | Record<string, unknown>): TutorBillingFilters => {
  const filters = emptyTutorBillingFilters()
  filters.search = getFirstQueryValue(query.q)
  filters.tutorIds = (normalizeArrayQuery(query.tutor_id) ?? [])
    .map(Number)
    .filter(id => Number.isInteger(id) && id > 0)
  filters.paymentServices = normalizeArrayQuery(query.payment_service) ?? []
  filters.tutorStatuses = toEnumValues(query.tutor_status, Object.values(TutorStatus))
  filters.changeType = toEnumValue(query.change_type, Object.values(TutorBillingChangeType))
  filters.datePreset = toEnumValue(query.change_date_preset, Object.values(TutorBillingDatePreset))
  if (filters.datePreset === TutorBillingDatePreset.Custom) {
    const dateFrom = toDateValue(query.change_date_from)
    const dateTo = toDateValue(query.change_date_to)
    const isValidRange = !!dateFrom && !!dateTo && dateFrom <= dateTo
    filters.dateFrom = isValidRange ? dateFrom : null
    filters.dateTo = isValidRange ? dateTo : null
  }
  TUTOR_BILLING_FLAGS.forEach((flag) => {
    filters.flags[flag] = parseTriState(query[flag])
  })
  return filters
}

export const filtersToQuery = (filters: TutorBillingFilters): LocationQueryRaw => {
  const query: LocationQueryRaw = {}
  if (filters.search) query.q = filters.search
  if (filters.tutorIds.length) query.tutor_id = filters.tutorIds.map(String)
  if (filters.paymentServices.length) query.payment_service = filters.paymentServices
  if (filters.tutorStatuses.length) query.tutor_status = filters.tutorStatuses
  if (filters.changeType) query.change_type = filters.changeType
  if (filters.datePreset) query.change_date_preset = filters.datePreset
  if (filters.datePreset === TutorBillingDatePreset.Custom && filters.dateFrom && filters.dateTo) {
    query.change_date_from = filters.dateFrom
    query.change_date_to = filters.dateTo
  }
  TUTOR_BILLING_FLAGS.forEach((flag) => {
    const value = serializeTriState(filters.flags[flag])
    if (value !== undefined) query[flag] = value
  })
  return query
}

export const isSameTutorBillingFilterQuery = (
  query: LocationQuery | Record<string, unknown>,
  otherQuery: LocationQuery | Record<string, unknown>,
): boolean => JSON.stringify(filtersToQuery(queryToFilters(query))) === JSON.stringify(filtersToQuery(queryToFilters(otherQuery)))
